import {
  FilterType,
  CustomFilterType,
  NativeFilterTypes,
} from 'design-system-web';
import { INVOICE_STATUS } from 'lane-shared/domains/billingPayments/types';
import { useTranslation } from 'react-i18next';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { getTimeZoneByGeoLocation } from 'async-timing';
import { useEffect, useRef } from 'react';
import { ExternalPayerType, InvoiceeInfo } from 'domains-billing-types';
import { searchWorkplaceMembersAndCompanies } from './searchWorkplaceMembersAndCompanies';
import { getCustomerNameById } from './getCustomerNameById';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { useSrInvoiceBilledStatusEnabled } from 'react-hooks';

const PER_PAGE = 50;

export const DEFAULT_FILTER_PARAMS = {
  page: 0,
  pageSize: PER_PAGE,
  total: 0,
};

export type InvoiceQueryString = {
  status: string;
  dueDate: string;
  invoiceDate: string;
  payer?: string;
  page?: string;
  pageSize?: string;
  total?: string;
};

export const useGetInvoiceTableFilters = (): (
  | FilterType
  | CustomFilterType<any>
)[] => {
  const { t } = useTranslation();
  const { channel } = useChannelAdminContext();
  const isPaymentsManagedExternallyEnabled = useFlag(
    FeatureFlag.WorkOrdersPaymentsManagedExternally,
    false
  );
  const isChargesFilteringV2Enabled = useFlag<boolean>(
    FeatureFlag.ChargesFilteringV2,
    false
  );
  const isBilledStatusEnabled = useSrInvoiceBilledStatusEnabled();
  const timeZone = channel?.address?.geo
    ? getTimeZoneByGeoLocation({
        longitude: channel.address.geo[0],
        latitude: channel.address.geo[1],
      })
    : '';

  const customerSearchInputRef = useRef<ReturnType<typeof searchWorkplaceMembersAndCompanies>>(
    async () => []
  );

  useEffect(() => {
    if (channel?._id) {
      customerSearchInputRef.current =
        searchWorkplaceMembersAndCompanies(channel._id);
    }
  }, [channel?._id]);

  const loadCustomerOptions = async (inputValue: string) => {
    const customers = await customerSearchInputRef.current(inputValue);

    const dropdownOptions = customers.map((customer: InvoiceeInfo) => {
      const prefix =
        customer.type === ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
          ? 'c-'
          : 'u-';

      return {
        label: customer.name,
        value: prefix + customer._id,
      };
    });

    return dropdownOptions;
  };

  const loadDefaultOptions = async (value: string) => {
    const prefix = value[0];
    const id = value.slice(2); // skip 'c-' or 'u-' to get the full id
    const customerType =
      prefix === 'c'
        ? ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_COMPANY
        : ExternalPayerType.EXTERNAL_PAYER_TYPE_ACTIVATE_USER;

    const customer = await getCustomerNameById(id, customerType);

    if (customer) {
      const dropdownOptions = [
        {
          label: customer.name,
          value,
        },
      ];

      return dropdownOptions;
    }

    return [];
  };

  const payerFilter = {
    key: 'payer',
    label: t('abp.invoiceList.customer'),
    type: NativeFilterTypes.Select as const,
    options: [],
    doTranslation: false,
    isPromoted: true,
    loadOptions: loadCustomerOptions,
    loadDefaultOptions,
    placeholder: t('abp.invoiceList.searchCustomers'),
    noOptionsMessage: t('abp.invoiceList.filter.startTyping'),
  };

  const tableFilters: (FilterType | CustomFilterType<any>)[] = [
    {
      key: 'status',
      label: t('abp.invoiceList.status'),
      type: NativeFilterTypes.Multiselect,
      options:
        getFilterableStatuses(
          Boolean(isChargesFilteringV2Enabled),
          isBilledStatusEnabled
        ) || [],
      isPromoted: true,
    },
    {
      key: 'invoiceDate',
      label: t('abp.invoiceList.issueDate'),
      type: NativeFilterTypes.DateRange,
      isPromoted: true,
      timeZone,
    },
    {
      key: 'dueDate',
      label: t('abp.invoiceList.dueDate'),
      type: NativeFilterTypes.DateRange,
      isPromoted: true,
      timeZone,
    },
    ...(isPaymentsManagedExternallyEnabled ? [payerFilter] : []),
  ];

  return tableFilters;
};

export const getFilterableStatuses = (
  isChargesFilteringV2Enabled: boolean,
  isBilledStatusEnabled: boolean
): { label: string; value: string }[] => {
  const statuses = [
    {
      label: INVOICE_STATUS.INVOICE_PENDING,
      value: 'CHARGE_ADDED',
    },
    {
      label: INVOICE_STATUS.INVOICE_UNPAID,
      value: 'INVOICED',
    },
    {
      label: INVOICE_STATUS.INVOICE_OVERDUE,
      value: 'OVERDUE',
    },
    // we dont have a void status, unpaid will show void which is not exactly the correct behavior
    // {
    //   label: INVOICE_STATUS.INVOICE_VOID,
    //   value: 'VOID',
    // },
    {
      label: INVOICE_STATUS.INVOICE_PAID,
      value: 'PAID',
    },
    {
      label: INVOICE_STATUS.INVOICE_PAYMENTFAILED,
      value: 'PAYMENT_FAILED',
    },
    ...(isChargesFilteringV2Enabled
      ? [
          {
            label: INVOICE_STATUS.INVOICE_EXTERNALLY_MANAGED,
            value: 'EXTERNALLY_MANAGED',
          },
        ]
      : []),
    ...(isBilledStatusEnabled
      ? [
          {
            label: INVOICE_STATUS.INVOICE_BILLED,
            value: 'BILLED',
          },
        ]
      : []),
  ];

  return statuses;
};
